import React from 'react';

import useGetProductsInformations from '@storefront/hooks/useGetProductsInformations';
import ModalSelection from '@storefront/shared-components/modals/modal-selection/modal-selection';
import {
  filterUnavailableInstantInkOptions,
  getProductImage,
  getProductSmallImage,
} from '@storefront/utils/prodRequests';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import OptionsColumns from '../options-columns/options-columns';
import styles from '../product-configuration.module.scss';
import { BundleItemWithLowestPrice } from '../product-configuration.utils';
import ImageRenderer from '../render-image';
import { NamespacesI18n } from '@storefront/i18n';

interface RenderOptionsProps {
  showSmallImage: boolean;
  loadingImage: boolean;
  setLoadingImage: (loading: boolean) => void;
  skuList: string[];
  isOptionSelected: boolean;
}

export default function RenderRequiredOptions(props: RenderOptionsProps) {
  const {
    showSmallImage,
    loadingImage,
    setLoadingImage,
    skuList,
    isOptionSelected,
  } = props;
  const { t } = useTranslation(NamespacesI18n.PDP_CONFIG);

  const { activeSKU, bundleProduct, bundleOptions, totalSteps } =
    useGetProductsInformations(skuList);

  const getImageRenderer = (option: BundleItemWithLowestPrice, id: string) => {
    const imageSizeHandler = showSmallImage
      ? getProductSmallImage(option)
      : getProductImage(option);
    return (
      <ImageRenderer
        src={imageSizeHandler}
        title={option.title}
        id={id}
        loadingImage={loadingImage}
        setLoadingImage={setLoadingImage}
        sku={bundleProduct?.sku}
        disabled={!isOptionSelected}
      />
    );
  };

  const containerId = 'required-options-container';

  return bundleOptions.visibleSimpleOptions.map((bundleOption, index) => (
    <div
      key={bundleOption.title}
      className={styles['products-options-container']}
      id={containerId}
    >
      {!showSmallImage && getImageRenderer(bundleOption, 'bundleOptionsImage')}
      <div
        className={classNames(
          styles['options'],
          !activeSKU && styles['options-disabled']
        )}
      >
        <div className={styles['label-container']}>
          <span className={styles['description']}>
            {t('radioButtonGroup.counter', {
              ith: index + 2,
              total: totalSteps,
            })}{' '}
          </span>
        </div>
        <span className={styles['title']}>
          {t('pdpConfig.printInkBundle.printSelectionTitle')}
        </span>
        {showSmallImage &&
          getImageRenderer(bundleOption, 'bundleOptionsTabletImage')}
        <div className={styles['options-column-container']}>
          {filterUnavailableInstantInkOptions(bundleOption.options)?.map(
            (availableOpt, index) => (
              <OptionsColumns
                key={availableOpt.uid}
                bundleOptionUid={bundleOption.uid}
                option={availableOpt}
                skuList={skuList}
                requiredOption
                index={index}
                parentId={containerId}
                bundleProduct={bundleProduct}
              />
            )
          )}
        </div>
        <div
          id="options-modal-container"
          data-testid="options-modal-container-test"
        >
          <ModalSelection
            modalName={bundleOption.options[0].product.hp_product_type_label}
            isDisabled={!activeSKU}
          />
        </div>
      </div>
    </div>
  ));
}
